/**
 * Tiny cache-with-fallback for the two runtime data sources.
 *
 * The rule everywhere in this app: a network call may improve the answer, but must
 * never be required to get one. You will be underground in a car park, in a stairwell,
 * or out of data — and that is exactly when you most need to know where to go next.
 * So every fetch here is wrapped so that a failure degrades to cached data, and a
 * cache miss degrades to a neutral default.
 */

const PREFIX = 'tlv-hotspots:';

interface CacheEntry<T> {
  value: T;
  storedAt: number;
}

export function readCache<T>(key: string, maxAgeMs: number): T | null {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (!raw) return null;
    const entry = JSON.parse(raw) as CacheEntry<T>;
    if (Date.now() - entry.storedAt > maxAgeMs) return null;
    return entry.value;
  } catch {
    return null;
  }
}

/** Read regardless of age — used as the last resort when the network is unavailable. */
export function readCacheStale<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (!raw) return null;
    return (JSON.parse(raw) as CacheEntry<T>).value;
  } catch {
    return null;
  }
}

export function writeCache<T>(key: string, value: T): void {
  try {
    const entry: CacheEntry<T> = { value, storedAt: Date.now() };
    localStorage.setItem(PREFIX + key, JSON.stringify(entry));
  } catch {
    // Storage full or blocked in private mode — the app works without the cache.
  }
}

/**
 * Fetch, cache, and fall back — in that order of preference:
 *   fresh cache -> network -> stale cache -> null
 */
export async function fetchWithFallback<T>(
  key: string,
  maxAgeMs: number,
  fetcher: () => Promise<T>,
): Promise<{ value: T | null; source: 'cache' | 'network' | 'stale' | 'none' }> {
  const fresh = readCache<T>(key, maxAgeMs);
  if (fresh !== null) return { value: fresh, source: 'cache' };

  try {
    const value = await fetcher();
    writeCache(key, value);
    return { value, source: 'network' };
  } catch {
    const stale = readCacheStale<T>(key);
    if (stale !== null) return { value: stale, source: 'stale' };
    return { value: null, source: 'none' };
  }
}
